import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setChaserSelectedAgents } from "store/slices/chaserDashboard";
import classNames from "classnames";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AssignAgentFilter } from "components/AgentDashboard/AssignAgentFilter";
import { SnoozeDialog } from "components/AgentDashboard/SnoozeDialog";
import { ChaserPreviewModal } from "./ChaserPreviewModal";
import { NewChasersFilterChip } from "./NewChasersFilterChip";
import { SnoozedItemsSection } from "./SnoozedItemsSection";
import {
  useGetAgentDashboardDetailsQuery,
  useGetChaserBatchesQuery,
  useGetAssignAgentsQuery,
} from "store/apis/agents";
import { useGetUserProfileQuery } from "store/apis/profile";
import {
  AgentActionItemType,
  ChaserBatch,
  ChaserBatchItem,
  ChaseItemCompanyGroup,
  ChaseAgentActionItemType,
} from "types/Models/agentDashboard";
import {
  AGENT_CHASE_ITEM,
  AGENT_FILING_CHAT_CARD_PENDING,
  AGENT_REVIEW_DRAFT_PENDING,
  AGENT_CHAT_ACTION_ITEM_PENDING,
  AGENT_CLIENT_DOCUMENT_PENDING,
  AGENT_ANSWER_REQUEST_PENDING,
  AGENT_INFO_REQUEST_PENDING,
  AGENT_FINANCIAL_REVIEW_REQUEST_PENDING,
  AGENT_DOCUMENT_UPLOADED_PENDING_REVIEW,
  AGENT_MEETING_REQUEST_PENDING,
} from "constants/agentDashboard";
import { AgentChatActionSlider } from "components/AgentDashboard/AgentChatActionSlider";
import { openLink } from "utils/openLink";
import { pluralize } from "utils/pluralize";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { MultiSelectFilter } from "components/DesignSystem/Filter/Filter";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ArrowDown } from "components/icons/ArrowDown";
import { CaretDown } from "components/icons/CaretDown";
import Loader from "components/DesignSystem/Loader/loader";
import { AnimatePresence } from "framer-motion";
import { useModal } from "hooks/useModal";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { DISPLAY_DATE, DISPLAY_DATE_HH_MM_A } from "constants/date";
import { useRoleBasedView } from "hooks/useRoleBasedView";

dayjs.extend(relativeTime);
dayjs.extend(utc);

// Matches backend: Tuesday=2, Friday=5 in dayjs UTC, send at 03:00 UTC
const CHASER_SEND_DAYS_UTC = new Set([2, 5]);
const CHASER_SEND_HOUR_UTC = 3;

const getNextSendWindow = (after: dayjs.Dayjs): dayjs.Dayjs => {
  const afterUtc = after.utc();
  for (let offset = 0; offset < 8; offset++) {
    const candidate = afterUtc.add(offset, "day");
    if (CHASER_SEND_DAYS_UTC.has(candidate.day())) {
      const window = candidate.startOf("day").add(CHASER_SEND_HOUR_UTC, "hour");
      if (window.isAfter(afterUtc)) return window;
    }
  }
  return afterUtc;
};

export const CHASE_TYPE_LABELS: Record<string, string> = {
  PENDING_MANDATORY_INVOICE: "Pending Invoice",
  FILING_CHAT_CARD_PENDING: "Esign Request",
  SUBSCRIPTION_FAILURE_DUE: "Subscription Failure",
  CLIENT_DOCUMENT_PENDING: "Document Request",
  REVIEW_DRAFT_PENDING: "Review Draft",
  CHAT_ACTION_ITEM_PENDING: "Chat Action Item",
  DOCUMENT_UPLOADED_PENDING_REVIEW: "Doc Awaiting Review",
  CART_ITEM_PENDING: "Cart Item",
  ANSWER_REQUEST_PENDING: "Answer Request",
  INFO_REQUEST_PENDING: "Open Item",
  TASK_UPLOAD_PENDING: "Upload Pending",
  FINANCIAL_REVIEW_REQUEST_PENDING: "Financial Review Request",
  MEETING_REQUEST_PENDING: "Meeting Request",
};

const TYPE_FILTER_OPTIONS = Object.entries(CHASE_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const CHAT_BASED_TYPES = new Set([
  AGENT_FILING_CHAT_CARD_PENDING,
  AGENT_REVIEW_DRAFT_PENDING,
  AGENT_CHAT_ACTION_ITEM_PENDING,
  AGENT_CLIENT_DOCUMENT_PENDING,
  AGENT_ANSWER_REQUEST_PENDING,
  AGENT_INFO_REQUEST_PENDING,
  AGENT_FINANCIAL_REVIEW_REQUEST_PENDING,
  AGENT_DOCUMENT_UPLOADED_PENDING_REVIEW,
  AGENT_MEETING_REQUEST_PENDING,
]);

export type ChatTarget = { channelId: string; messageId?: string } | null;

const getChaseItemTarget = (item: ChaseAgentActionItemType) => {
  const itemLink = (item as AgentActionItemType).link;
  if (CHAT_BASED_TYPES.has(item.metric_type)) {
    if (item.channel_url) {
      return {
        type: "chat" as const,
        channelId: item.channel_url,
        messageId: item.message_id,
      };
    }
    if (itemLink) return { type: "url" as const, url: itemLink };
    return null;
  }
  return itemLink ? { type: "url" as const, url: itemLink } : null;
};

type SnoozeState = {
  companyGroupId: string;
  metricType: string;
  itemUuid: string;
} | null;

type CompanyGroup = {
  companyGroup: ChaseItemCompanyGroup;
  currentBusItems: AgentActionItemType[];
  upcomingItems: AgentActionItemType[];
  batch?: ChaserBatch;
};

// ─── Filters ────────────────────────────────────────────────────────────────

const TypeFilter = ({
  selectedTypes,
  setSelectedTypes,
}: {
  selectedTypes: string[];
  setSelectedTypes: (val: string[]) => void;
}) => {
  const { isOpen, toggle } = useModal();

  const selectedLabels = useMemo(
    () =>
      TYPE_FILTER_OPTIONS.filter((opt) =>
        selectedTypes.includes(opt.value)
      ).map((opt) => opt.label),
    [selectedTypes]
  );

  const displayText =
    selectedLabels.length > 0 ? selectedLabels.join(", ") : "All types";

  return (
    <Dropdown.Root open={isOpen} onOpenChange={toggle}>
      <ConditionalToolTip
        text={selectedLabels.join(", ")}
        condition={selectedLabels.length > 0}
        propClass="t-max-w-64"
      >
        <Dropdown.Trigger asChild>
          <button
            type="button"
            className="all:unset t-flex t-items-center t-gap-2 t-px-3 t-py-1.5 t-border t-border-solid t-border-surface-grey t-rounded-lg t-bg-surface-lighter-grey hover:t-border-purple-30 hover:t-bg-neutral-0 t-cursor-pointer t-w-36"
          >
            <span className="t-truncate t-text-body-sm t-flex-1 t-text-left">
              {displayText}
            </span>
            <ArrowDown strokeWidth="1.5" color="currentColor" />
          </button>
        </Dropdown.Trigger>
      </ConditionalToolTip>
      <Dropdown.Portal>
        <AnimatePresence>
          {isOpen && (
            <Dropdown.Content asChild sideOffset={8} align="end">
              <div className="t-max-h-64 t-overflow-auto">
                <MultiSelectFilter
                  isSearchable
                  isSelectAll
                  onChange={setSelectedTypes}
                  checkboxSpace="small"
                  options={TYPE_FILTER_OPTIONS}
                  selected={selectedTypes}
                  className="t-p-2"
                />
              </div>
            </Dropdown.Content>
          )}
        </AnimatePresence>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

// ─── Table layout ────────────────────────────────────────────────────────────

export const COLUMN_CLASSES = {
  date: "t-w-32 t-shrink-0",
  chaseCount: "t-w-20 t-shrink-0 t-text-center",
  actionType: "t-w-40 t-shrink-0",
  description: "t-flex-1 t-min-w-0",
  actions: "t-w-20 t-shrink-0 t-text-right",
};

export const ColumnHeaders = () => (
  <div className="t-flex t-items-center t-gap-4 t-px-4 t-py-2 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey">
    <span
      className={classNames(
        COLUMN_CLASSES.date,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Created
    </span>
    <span
      className={classNames(
        COLUMN_CLASSES.chaseCount,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Chased
    </span>
    <span
      className={classNames(
        COLUMN_CLASSES.actionType,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Action Type
    </span>
    <span
      className={classNames(
        COLUMN_CLASSES.description,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Description
    </span>
    <span
      className={classNames(
        COLUMN_CLASSES.actions,
        "t-text-caption t-font-medium t-text-text-30"
      )}
    >
      Actions
    </span>
  </div>
);

const ChaseItemRow = ({
  item,
  companyGroupId,
  onSnooze,
  onNavigate,
}: {
  item: AgentActionItemType;
  companyGroupId: string;
  onSnooze: (state: SnoozeState) => void;
  onNavigate: (target: ChatTarget) => void;
}) => {
  const label = CHASE_TYPE_LABELS[item.metric_type] ?? item.metric_type;
  const target = getChaseItemTarget(item as ChaseAgentActionItemType);
  const dateStr = item.created_at
    ? dayjs(item.created_at).format(DISPLAY_DATE)
    : "—";
  let description = item.detail;
  if (!description) {
    if (item.metric_type === AGENT_INFO_REQUEST_PENDING) {
      description = "Transaction Categorisation";
    } else if (item.metric_type === AGENT_ANSWER_REQUEST_PENDING) {
      description = "Information requested";
    } else {
      description = "—";
    }
  }

  const handleRowClick = () => {
    if (!target) return;
    if (target.type === "chat") {
      onNavigate({ channelId: target.channelId, messageId: target.messageId });
    } else {
      openLink(target.url, "_blank");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (target && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleRowClick();
    }
  };

  return (
    <div
      className={classNames(
        "t-flex t-items-center t-gap-4 t-px-4 t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-10 last:t-border-b-0 hover:t-bg-surface-lighter-grey",
        { "t-cursor-pointer": !!target }
      )}
      onClick={target ? handleRowClick : undefined}
      onKeyDown={target ? handleKeyDown : undefined}
      role={target ? "button" : undefined}
      tabIndex={target ? 0 : undefined}
    >
      <span
        className={classNames(
          COLUMN_CLASSES.date,
          "t-text-body-sm t-text-text-60"
        )}
      >
        {dateStr}
      </span>
      <span
        className={classNames(
          COLUMN_CLASSES.chaseCount,
          "t-text-body-sm t-text-text-60"
        )}
      >
        {item.chase_count ?? 0}
      </span>
      <span
        className={classNames(
          COLUMN_CLASSES.actionType,
          "t-text-body-sm t-text-text-60"
        )}
      >
        {label}
      </span>
      <span
        className={classNames(
          COLUMN_CLASSES.description,
          "t-text-body-sm t-text-text-80 t-truncate"
        )}
      >
        {description}
      </span>
      <span className={COLUMN_CLASSES.actions}>
        <Button
          customType="ghost"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSnooze({
              companyGroupId,
              metricType: item.metric_type,
              itemUuid: item.uuid,
            });
          }}
        >
          Snooze
        </Button>
      </span>
    </div>
  );
};

// ─── Section headers ─────────────────────────────────────────────────────────

const CurrentChasersSectionHeader = ({
  scheduledSendAt,
}: {
  scheduledSendAt: string | null;
}) => (
  <div className="t-flex t-items-end t-justify-between t-pb-3">
    <div>
      <p className="t-text-caption t-font-medium t-text-text-30 t-uppercase t-tracking-wide t-mb-0.5">
        Current Chasers
      </p>
      {scheduledSendAt && (
        <p className="t-text-body-sm t-text-text-30">
          Sending out{" "}
          <span className="t-text-text-60">
            {dayjs(scheduledSendAt).format("dddd, MMMM D, YYYY [at] h:mm A")}
          </span>
        </p>
      )}
    </div>
  </div>
);

const UpcomingSectionHeader = ({
  nextSendAt,
}: {
  nextSendAt: string | null;
}) => (
  <div className="t-pb-3">
    <p className="t-text-caption t-font-medium t-text-text-30 t-uppercase t-tracking-wide t-mb-0.5">
      Upcoming Chasers
    </p>
    {nextSendAt && (
      <p className="t-text-body-sm t-text-text-30">
        Will send{" "}
        <span className="t-text-text-60">
          {dayjs(nextSendAt).format("dddd, MMMM D, YYYY [at] h:mm A")}
        </span>
      </p>
    )}
  </div>
);

// ─── Filtering ────────────────────────────────────────────────────────────────

export const filterChaseItems = <
  T extends { metric_type: string; chase_count?: number }
>(
  items: T[],
  selectedTypes: string[],
  newOnly: boolean
): T[] => {
  const byType =
    selectedTypes.length === 0
      ? items
      : items.filter((i) => selectedTypes.includes(i.metric_type));
  return newOnly ? byType.filter((i) => (i.chase_count ?? 0) === 0) : byType;
};

// ─── Accordion helpers ───────────────────────────────────────────────────────

// Wraps RawTrigger so the caret + base styles are shared without nesting a <button> in a <button>.
export const GroupTriggerRow = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <Accordion.RawTrigger asChild>
    <button className="all:unset t-flex-1 t-group t-flex t-items-center t-justify-between t-gap-4 t-p-4 t-text-subtext t-cursor-pointer t-select-none">
      {children}
      <span className="t-transform t-transition t-duration-300 t-ease-in-out group-data-state-open:-t-rotate-180 t-shrink-0 t-text-neutral">
        <CaretDown />
      </span>
    </button>
  </Accordion.RawTrigger>
);

// ─── Company group accordions ─────────────────────────────────────────────────

const CompanyGroupAccordionItem = ({
  group,
  onPreview,
  onSnooze,
  onNavigate,
  selectedTypes,
  newOnly,
}: {
  group: CompanyGroup;
  onPreview: (batch: ChaserBatch) => void;
  onSnooze: (state: SnoozeState) => void;
  onNavigate: (target: ChatTarget) => void;
  selectedTypes: string[];
  newOnly: boolean;
}) => {
  const { companyGroup, currentBusItems, batch } = group;

  const handlePreview = () => {
    if (!batch) return;
    const liveUuids = new Set(currentBusItems.map((i) => i.uuid));
    onPreview({
      ...batch,
      items: batch.items.filter((i) => liveUuids.has(i.item_uuid)),
    });
  };
  const agentSuffix = companyGroup.agent_name
    ? ` · ${companyGroup.agent_name}`
    : "";

  const lastSentStr = batch?.last_sent_at
    ? dayjs(batch.last_sent_at).format(DISPLAY_DATE_HH_MM_A)
    : "—";

  const filteredItems = filterChaseItems(
    currentBusItems,
    selectedTypes,
    newOnly
  );

  if (filteredItems.length === 0) return null;

  return (
    <Accordion.Item value={companyGroup.uuid}>
      {/*
        Accordion.Header renders as <h3>. Keeping the preview button as a sibling
        of GroupTriggerRow avoids the invalid button-in-button HTML issue.
      */}
      <Accordion.Header className="t-m-0 t-flex t-items-stretch">
        <GroupTriggerRow>
          <div className="t-flex t-items-center t-gap-1 t-min-w-0">
            <span className="t-text-subtext t-font-semibold t-text-text-100">
              {companyGroup.name}
            </span>
            {agentSuffix && (
              <span className="t-text-body-sm t-text-text-30">
                {agentSuffix}
              </span>
            )}
          </div>
          <div className="t-flex t-items-center t-gap-4 t-shrink-0">
            <span className="t-text-body-sm t-text-text-30">
              {pluralize(filteredItems.length, "Item", "Items")}
            </span>
            <span className="t-text-body-sm t-text-text-30">
              Last sent: <span className="t-text-text-60">{lastSentStr}</span>
            </span>
          </div>
        </GroupTriggerRow>

        {batch && (
          <div className="t-flex t-items-center t-px-4 t-shrink-0 t-border-0 t-border-l t-border-solid t-border-neutral-10">
            <Button customType="secondary" size="small" onClick={handlePreview}>
              Preview message
            </Button>
          </div>
        )}
      </Accordion.Header>

      <Accordion.Content className="t-p-0">
        <ColumnHeaders />
        <div className="t-flex t-flex-col">
          {filteredItems.map((item) => (
            <ChaseItemRow
              key={`${item.metric_type}-${item.uuid}`}
              item={item}
              companyGroupId={companyGroup.uuid}
              onSnooze={onSnooze}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

const NoBatchGroupItem = ({
  companyGroup,
  items,
  lastSentAt,
  onSnooze,
  onNavigate,
  selectedTypes,
  newOnly,
}: {
  companyGroup: ChaseItemCompanyGroup;
  items: AgentActionItemType[];
  lastSentAt: string | null;
  onSnooze: (state: SnoozeState) => void;
  onNavigate: (target: ChatTarget) => void;
  selectedTypes: string[];
  newOnly: boolean;
}) => {
  const agentSuffix = companyGroup.agent_name
    ? ` · ${companyGroup.agent_name}`
    : "";

  const lastSentStr = lastSentAt
    ? dayjs(lastSentAt).format(DISPLAY_DATE_HH_MM_A)
    : "—";

  const filtered = filterChaseItems(items, selectedTypes, newOnly);

  if (filtered.length === 0) return null;

  return (
    <Accordion.Item value={companyGroup.uuid}>
      <Accordion.Header className="t-m-0 t-flex t-items-stretch">
        <GroupTriggerRow>
          <div className="t-flex t-items-center t-gap-1 t-min-w-0">
            <span className="t-text-subtext t-font-semibold t-text-text-100">
              {companyGroup.name}
            </span>
            {agentSuffix && (
              <span className="t-text-body-sm t-text-text-30">
                {agentSuffix}
              </span>
            )}
          </div>
          <div className="t-flex t-items-center t-gap-4 t-shrink-0">
            <span className="t-text-body-sm t-text-text-30">
              {pluralize(filtered.length, "Item", "Items")}
            </span>
            <span className="t-text-body-sm t-text-text-30">
              Last sent: <span className="t-text-text-60">{lastSentStr}</span>
            </span>
          </div>
        </GroupTriggerRow>
      </Accordion.Header>

      <Accordion.Content className="t-p-0">
        <ColumnHeaders />
        <div className="t-flex t-flex-col">
          {filtered.map((item) => (
            <ChaseItemRow
              key={`${item.metric_type}-${item.uuid}`}
              item={item}
              companyGroupId={companyGroup.uuid}
              onSnooze={onSnooze}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

// ─── Upcoming tier-2 batch group (snoozed items from last-sent batch) ─────────

const UpcomingBatchGroupItem = ({
  batch,
  selectedTypes,
  newOnly,
  onNavigate,
}: {
  batch: ChaserBatch;
  selectedTypes: string[];
  newOnly: boolean;
  onNavigate: (target: ChatTarget) => void;
}) => {
  const { company_group, items, last_sent_at } = batch;
  const agentSuffix = company_group.agent_name
    ? ` · ${company_group.agent_name}`
    : "";
  const lastSentStr = last_sent_at
    ? dayjs(last_sent_at).format(DISPLAY_DATE_HH_MM_A)
    : "—";

  const itemsWithLinks = items.filter((i) =>
    Boolean(i.item_channel_url || i.link)
  );

  const filtered = filterChaseItems(itemsWithLinks, selectedTypes, newOnly);

  if (filtered.length === 0) return null;

  const getTarget = (item: ChaserBatchItem) => {
    if (item.item_channel_url) {
      // link format: {base_url}chat/{channel_url}/{message_id}
      const afterChannel = item.link?.split(`${item.item_channel_url}/`)[1];
      const messageId = afterChannel?.split("/")[0] || undefined;
      return {
        type: "chat" as const,
        channelId: item.item_channel_url,
        messageId,
      };
    }
    return item.link ? { type: "url" as const, url: item.link } : null;
  };

  return (
    <Accordion.Item value={`upcoming-${company_group.uuid}`}>
      <Accordion.Header className="t-m-0 t-flex t-items-stretch">
        <GroupTriggerRow>
          <div className="t-flex t-items-center t-gap-1 t-min-w-0">
            <span className="t-text-subtext t-font-semibold t-text-text-100">
              {company_group.name}
            </span>
            {agentSuffix && (
              <span className="t-text-body-sm t-text-text-30">
                {agentSuffix}
              </span>
            )}
          </div>
          <div className="t-flex t-items-center t-gap-4 t-shrink-0">
            <span className="t-text-body-sm t-text-text-30">
              {pluralize(filtered.length, "Item", "Items")}
            </span>
            <span className="t-text-body-sm t-text-text-30">
              Last sent: <span className="t-text-text-60">{lastSentStr}</span>
            </span>
          </div>
        </GroupTriggerRow>
      </Accordion.Header>

      <Accordion.Content className="t-p-0">
        <ColumnHeaders />
        <div className="t-flex t-flex-col">
          {filtered.map((item) => {
            const label =
              CHASE_TYPE_LABELS[item.metric_type] ?? item.metric_type;
            const target = getTarget(item);
            const dateStr = item.created_at
              ? dayjs(item.created_at).format(DISPLAY_DATE)
              : "—";
            const description = item.detail || "—";

            const handleClick = () => {
              if (!target) return;
              if (target.type === "chat") {
                onNavigate({
                  channelId: target.channelId,
                  messageId: target.messageId,
                });
              } else {
                openLink(target.url, "_blank");
              }
            };

            return (
              <div
                key={`${item.metric_type}-${item.item_uuid}`}
                className={classNames(
                  "t-flex t-items-center t-gap-4 t-px-4 t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-10 last:t-border-b-0 hover:t-bg-surface-lighter-grey",
                  { "t-cursor-pointer": !!target }
                )}
                onClick={target ? handleClick : undefined}
                onKeyDown={
                  target
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleClick();
                        }
                      }
                    : undefined
                }
                role={target ? "button" : undefined}
                tabIndex={target ? 0 : undefined}
              >
                <span
                  className={classNames(
                    COLUMN_CLASSES.date,
                    "t-text-body-sm t-text-text-60"
                  )}
                >
                  {dateStr}
                </span>
                <span
                  className={classNames(
                    COLUMN_CLASSES.chaseCount,
                    "t-text-body-sm t-text-text-60"
                  )}
                >
                  {item.chase_count ?? 0}
                </span>
                <span
                  className={classNames(
                    COLUMN_CLASSES.actionType,
                    "t-text-body-sm t-text-text-60"
                  )}
                >
                  {label}
                </span>
                <span
                  className={classNames(
                    COLUMN_CLASSES.description,
                    "t-text-body-sm t-text-text-80 t-truncate"
                  )}
                >
                  {description}
                </span>
                <span className={COLUMN_CLASSES.actions} />
              </div>
            );
          })}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

// ─── Tab content ──────────────────────────────────────────────────────────────

const ChaseItemsTab = ({
  selectedAgents,
  isAgentSelectionReady,
  selectedTypes,
  setSelectedTypes,
  newOnly,
  onPreview,
  onSnooze,
  onNavigate,
}: {
  selectedAgents: string[];
  isAgentSelectionReady: boolean;
  selectedTypes: string[];
  setSelectedTypes: (val: string[]) => void;
  newOnly: boolean;
  onPreview: (batch: ChaserBatch) => void;
  onSnooze: (state: SnoozeState) => void;
  onNavigate: (target: ChatTarget) => void;
}) => {
  const agentUserIds =
    selectedAgents.length > 0 ? selectedAgents.join(",") : null;

  const { data: liveItems = [], isFetching } = useGetAgentDashboardDetailsQuery(
    {
      actionType: AGENT_CHASE_ITEM,
      agentUserIds,
    },
    { skip: !isAgentSelectionReady }
  );

  const { data: batches = [], isFetching: isFetchingBatches } =
    useGetChaserBatchesQuery(agentUserIds ? { agentUserIds } : undefined, {
      skip: !isAgentSelectionReady,
    });

  const earliestBatch = useMemo(() => {
    if (batches.length === 0) return null;
    return batches.reduce((earliest, b) =>
      b.scheduled_send_at < earliest.scheduled_send_at ? b : earliest
    );
  }, [batches]);

  const busSendAt = earliestBatch?.scheduled_send_at ?? null;

  // Tier-1: batches at the earliest (current) send window.
  // Tier-2: batches scheduled beyond busSendAt — snoozed items from the last-sent cycle.
  const { tier1Batches, tier2Batches } = useMemo(() => {
    const tier1: ChaserBatch[] = [];
    const tier2: ChaserBatch[] = [];
    for (const b of batches) {
      if (b.scheduled_send_at === busSendAt) {
        tier1.push(b);
      } else {
        tier2.push(b);
      }
    }
    return { tier1Batches: tier1, tier2Batches: tier2 };
  }, [batches, busSendAt]);

  // Build lookup only from tier-1 (current) batches.
  const batchByGroup = useMemo(() => {
    const map: Record<
      string,
      { batch: ChaserBatch; inBatchUuids: Set<string> }
    > = {};
    for (const batch of tier1Batches) {
      map[batch.company_group.uuid] = {
        batch,
        inBatchUuids: new Set(batch.items.map((i) => i.item_uuid)),
      };
    }
    return map;
  }, [tier1Batches]);

  // Split live items into groups-with-batch (current bus) vs groups-without-batch (upcoming).
  const { groupsWithBatch, groupsWithoutBatch } = useMemo(() => {
    const withBatch: Record<string, CompanyGroup> = {};
    const withoutBatch: Record<
      string,
      { companyGroup: ChaseItemCompanyGroup; items: AgentActionItemType[] }
    > = {};

    for (const item of liveItems.filter((i) => {
      if (!(i.metric_type in CHASE_TYPE_LABELS)) return false;
      if (CHAT_BASED_TYPES.has(i.metric_type)) {
        const chatItem = i as ChaseAgentActionItemType;
        return Boolean(chatItem.channel_url || i.link);
      }
      return Boolean(i.link);
    })) {
      if (!item.company_group) continue;
      const groupUuid = item.company_group.uuid;
      const batchEntry = batchByGroup[groupUuid];

      if (batchEntry) {
        if (!withBatch[groupUuid]) {
          withBatch[groupUuid] = {
            companyGroup: item.company_group,
            currentBusItems: [],
            upcomingItems: [],
            batch: batchEntry.batch,
          };
        }
        if (batchEntry.inBatchUuids.has(item.uuid)) {
          withBatch[groupUuid].currentBusItems.push(item);
        } else {
          withBatch[groupUuid].upcomingItems.push(item);
        }
      } else {
        if (!withoutBatch[groupUuid]) {
          withoutBatch[groupUuid] = {
            companyGroup: item.company_group,
            items: [],
          };
        }
        withoutBatch[groupUuid].items.push(item);
      }
    }

    return {
      groupsWithBatch: Object.values(withBatch),
      groupsWithoutBatch: Object.values(withoutBatch),
    };
  }, [liveItems, batchByGroup]);

  // Upcoming = items not in any current (tier-1) batch, split into three sources:
  //   1. Items from tier-1 batch-groups that arrived after the batch was locked.
  //   2. Items from groups with no pending batch (waiting for the next collection run).
  // Tier-2 batches (snoozed items from the last-sent cycle) are rendered separately.
  const upcomingGroups = useMemo(
    () => [
      ...groupsWithBatch
        .filter((g) => g.upcomingItems.length > 0)
        .map((g) => ({
          companyGroup: g.companyGroup,
          items: g.upcomingItems,
          lastSentAt: g.batch?.last_sent_at ?? null,
        })),
      ...groupsWithoutBatch.map((g) => ({ ...g, lastSentAt: null })),
    ],
    [groupsWithBatch, groupsWithoutBatch]
  );

  const hasUpcoming = upcomingGroups.length > 0 || tier2Batches.length > 0;

  const currentWindowAt = getNextSendWindow(dayjs()).toISOString();
  const upcomingWindowAt = getNextSendWindow(
    getNextSendWindow(dayjs()).add(1, "minute")
  ).toISOString();

  if (!isAgentSelectionReady || isFetching || isFetchingBatches) {
    return <Loader />;
  }

  return (
    <div className="t-px-5 t-flex t-flex-col t-gap-6">
      <div className="t-border t-border-solid t-border-neutral-10 t-rounded-xl t-overflow-hidden">
        <section className="t-p-4">
          <CurrentChasersSectionHeader scheduledSendAt={currentWindowAt} />
          {groupsWithBatch.length > 0 ? (
            <Accordion.Root
              type="single"
              collapsible
              className="t-flex t-flex-col t-gap-3"
            >
              {groupsWithBatch.map((group) => (
                <CompanyGroupAccordionItem
                  key={group.companyGroup.uuid}
                  group={group}
                  onPreview={onPreview}
                  onSnooze={onSnooze}
                  onNavigate={onNavigate}
                  selectedTypes={selectedTypes}
                  newOnly={newOnly}
                />
              ))}
            </Accordion.Root>
          ) : (
            <div className="t-text-body-sm t-text-text-30 t-py-2">
              0 chase items
            </div>
          )}
        </section>
      </div>

      <div className="t-border t-border-solid t-border-neutral-10 t-rounded-xl t-overflow-hidden">
        <section className="t-p-4">
          <UpcomingSectionHeader nextSendAt={upcomingWindowAt} />
          {hasUpcoming ? (
            <Accordion.Root
              type="single"
              collapsible
              className="t-flex t-flex-col t-gap-3"
            >
              {upcomingGroups.map(({ companyGroup, items, lastSentAt }) => (
                <NoBatchGroupItem
                  key={companyGroup.uuid}
                  companyGroup={companyGroup}
                  items={items}
                  lastSentAt={lastSentAt}
                  onSnooze={onSnooze}
                  onNavigate={onNavigate}
                  selectedTypes={selectedTypes}
                  newOnly={newOnly}
                />
              ))}
              {tier2Batches.map((batch) => (
                <UpcomingBatchGroupItem
                  key={`tier2-${batch.uuid}`}
                  batch={batch}
                  selectedTypes={selectedTypes}
                  newOnly={newOnly}
                  onNavigate={onNavigate}
                />
              ))}
            </Accordion.Root>
          ) : (
            <div className="t-text-body-sm t-text-text-30 t-py-2">
              0 chase items
            </div>
          )}
        </section>
      </div>

      <SnoozedItemsSection
        agentUserIds={agentUserIds}
        isAgentSelectionReady={isAgentSelectionReady}
        selectedTypes={selectedTypes}
        newOnly={newOnly}
        onNavigate={onNavigate}
      />
    </div>
  );
};

// ─── Page root ────────────────────────────────────────────────────────────────

export const ChaserDashboard = () => {
  const [manualAgentSelection, setManualAgentSelection] = useState<
    string[] | null
  >(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [newOnly, setNewOnly] = useState(true);
  const [previewBatch, setPreviewBatch] = useState<ChaserBatch | null>(null);
  const [snoozeState, setSnoozeState] = useState<SnoozeState>(null);
  const [chatTarget, setChatTarget] = useState<ChatTarget>(null);
  const { isAdmin } = useRoleBasedView();

  const { data: userProfile } = useGetUserProfileQuery();
  const { isSuccess: agentsLoaded } = useGetAssignAgentsQuery(
    {},
    { skip: !isAdmin }
  );

  const defaultAgentIds = useMemo(() => {
    if (!userProfile?.uuid || !agentsLoaded) return [];
    return [userProfile.uuid];
  }, [userProfile?.uuid, agentsLoaded]);

  const selectedAgents = manualAgentSelection ?? defaultAgentIds;
  const isAgentSelectionReady =
    manualAgentSelection !== null ||
    (Boolean(userProfile?.uuid) && agentsLoaded);
  const dispatch = useDispatch();

  const setSelectedAgents = (value: string[]) => {
    setManualAgentSelection(value);
    dispatch(setChaserSelectedAgents(value));
  };

  return (
    <DashboardContainer className="t-h-full">
      <Header v2 title="Chase Items" />

      <DashboardContainer.Content>
        <div className="t-flex t-items-center t-justify-between t-gap-4 t-px-4 t-pb-4 sm:t-flex-wrap sm:t-gap-2">
          <NewChasersFilterChip
            isActive={newOnly}
            onActivate={() => setNewOnly(true)}
            onDeactivate={() => setNewOnly(false)}
          />
          <div className="t-flex t-items-center t-gap-2 t-shrink-0 sm:t-shrink sm:t-flex-wrap">
            <AssignAgentFilter
              selectedAgents={selectedAgents}
              setSelectedAgents={setSelectedAgents}
            />
            <TypeFilter
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
            />
          </div>
        </div>
        <ChaseItemsTab
          selectedAgents={selectedAgents}
          isAgentSelectionReady={isAgentSelectionReady}
          selectedTypes={selectedTypes}
          setSelectedTypes={setSelectedTypes}
          newOnly={newOnly}
          onPreview={setPreviewBatch}
          onSnooze={setSnoozeState}
          onNavigate={setChatTarget}
        />
      </DashboardContainer.Content>

      {previewBatch && (
        <ChaserPreviewModal
          isOpen
          onClose={() => setPreviewBatch(null)}
          batch={previewBatch}
          onViewChannel={(channelId) => {
            setPreviewBatch(null);
            setChatTarget({ channelId });
          }}
        />
      )}

      {snoozeState && (
        <SnoozeDialog
          isOpen
          onClose={() => setSnoozeState(null)}
          companyGroupId={snoozeState.companyGroupId}
          metricType={snoozeState.metricType}
          itemUuid={snoozeState.itemUuid}
        />
      )}

      {chatTarget && (
        <AgentChatActionSlider
          isOpen
          close={() => setChatTarget(null)}
          channelId={chatTarget.channelId}
          messageId={chatTarget.messageId}
        />
      )}
    </DashboardContainer>
  );
};
